import * as React from 'react';
import { Tooltip } from '@mui/material';
import { cv } from '../../themeVars';

// Centralized chat tag/chip colors. Each value is the exact hex that was
// previously inlined across ConversationsList and ChatScreen, so swapping a
// literal for the token is visually a no-op. Change a color here once and every
// chip updates. Each is wrapped in cv() so the chat dark mode can swap it
// (see themeVars.js); in light mode it resolves to the same hex.
export const chatColors = {
    tourTagDeparture: cv('tag-blue', '#EAF4FF'),
    tourTag: cv('tag-tan', '#FDF3E1'),
    chatTag: cv('tag-lavender', '#E4E4F9'),
    internalTag: cv('tag-pink', '#F8D7DA'),
    paxBg: cv('tag-green', '#E1F0E6'),
    paxText: cv('tag-green-text', '#184E31'),
    checkoutBg: cv('tag-slate', '#E9EDF3'),
    checkoutText: cv('text', '#253447'),
    checkoutPastBg: cv('surface-muted', '#F1F1F1'),
    checkoutPastText: cv('text-muted', '#7A7A7A'),
    textDark: cv('text', '#202020'),
    textBlack: cv('text', 'black'),
};

// --- Style builders ---------------------------------------------------------
// Each returns the exact style object that was previously written inline, just
// with the color literals sourced from `chatColors`. Centralizing them removes
// the per-copy drift that made chips render 1px / a shade off from each other.

// List-row tour tag (embedded / "left panel" rows): .88em, flex-gap spacing
// (no margin), inline-block. `isDeparture` picks the blue vs tan background.
export const rowTourTagStyle = (unread, isDeparture) => ({
    fontSize: '.88em',
    padding: '1px 4px 1px 4px',
    color: chatColors.textBlack,
    fontWeight: unread ? 500 : 400,
    background: isDeparture ? chatColors.tourTagDeparture : chatColors.tourTag,
    borderRadius: 3,
    display: 'inline-block',
});

// List-row chat tag. `dense` (the embedded / left panel) uses 1px vertical
// padding and inherits line-height; the standard panel uses 2px + lineHeight 1.15.
export const rowChatTagStyle = (unread, dense) => ({
    fontSize: '.88em',
    padding: dense ? '1px 4px 1px 4px' : '2px 4px 2px 4px',
    color: chatColors.textBlack,
    fontWeight: unread ? 500 : 400,
    background: chatColors.chatTag,
    borderRadius: 3,
    ...(dense ? {} : { lineHeight: 1.15 }),
    display: 'inline-block',
});

// List-row PAX / +1 chip (BookingIndicators). `background` is passed in so the
// caller keeps control of the lead-booker vs party-member color.
export const rowPaxStyle = (unread, dense, background) => ({
    fontSize: '.88em',
    padding: dense ? '1px 4px 1px 4px' : '2px 4px 2px 4px',
    color: chatColors.paxText,
    background,
    borderRadius: 3,
    fontWeight: unread ? 500 : 400,
    ...(dense ? {} : { lineHeight: 1.15 }),
    whiteSpace: 'nowrap',
    display: 'inline-block',
});

// Header / subtext tour tag (ConversationsList + ChatScreen headers): .94em,
// inline, marginLeft spacing.
export const headerTourTagStyle = (isDeparture) => ({
    fontSize: '.94em',
    padding: 2,
    marginLeft: 4,
    color: chatColors.textDark,
    background: isDeparture ? chatColors.tourTagDeparture : chatColors.tourTag,
    borderRadius: 3,
});

// Header / subtext chat tag.
export const headerChatTagStyle = () => ({
    fontSize: '.94em',
    padding: 2,
    marginLeft: 4,
    color: chatColors.textDark,
    background: chatColors.chatTag,
    borderRadius: 3,
});

// Header / subtext internal tag (ChatScreen): same as the chat tag but with
// horizontal padding of 4.
export const headerInternalTagStyle = () => ({
    fontSize: '.94em',
    padding: 2,
    paddingLeft: 4,
    paddingRight: 4,
    marginLeft: 4,
    color: chatColors.textDark,
    background: chatColors.internalTag,
    borderRadius: 3,
});

// Header / subtext PAX chip. ConversationsList's total-pax chip uses the plain
// form; ChatScreen's lead-booker chip uses `nowrap` (horizontal padding 4 +
// whiteSpace nowrap).
export const headerPaxStyle = (nowrap = false) => ({
    fontSize: '.94em',
    padding: 2,
    ...(nowrap ? { paddingLeft: 4, paddingRight: 4 } : {}),
    marginLeft: 4,
    color: chatColors.paxText,
    background: chatColors.paxBg,
    borderRadius: 3,
    ...(nowrap ? { whiteSpace: 'nowrap' } : {}),
});

// Thin wrapper reproducing the existing `<Tooltip arrow placement="top"><span
// style=...>` chip markup so every chip shares one structure. Output is
// identical to the previous inline markup.
export function Tag({ title, style, children }) {
    return (
        <Tooltip title={title ?? ''} arrow placement="top">
            <span style={style}>{children}</span>
        </Tooltip>
    );
}
